import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";

export default function AdminAccountsView() {
  const [accounts, setAccounts] = useState(null);
  const [pins, setPins] = useState({});
  const [creds, setCreds] = useState({});
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api("/api/admin/accounts");
      setAccounts(data);
    } catch (err) {
      toast(formatApiError(err.message));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const savePin = async (key) => {
    const pin = (pins[key] || "").trim();
    setSaving(`pin-${key}`);
    try {
      await api(`/api/admin/accounts/${key}/pin`, {
        method: "POST",
        body: JSON.stringify({ pin }),
      });
      setPins((p) => ({ ...p, [key]: "" }));
      toast(`PIN untuk ${key} berhasil disimpan.`);
      await load();
    } catch (err) {
      toast(formatApiError(err.message));
    } finally {
      setSaving(null);
    }
  };

  const saveCred = async (key) => {
    const c = creds[key] || {};
    setSaving(`cred-${key}`);
    try {
      await api(`/api/admin/accounts/${key}/credentials`, {
        method: "POST",
        body: JSON.stringify({
          username: (c.username || "").trim(),
          password: c.password || "",
        }),
      });
      setCreds((p) => ({ ...p, [key]: { username: "", password: "" } }));
      toast(`Username & password untuk ${key} berhasil disimpan.`);
      await load();
    } catch (err) {
      toast(formatApiError(err.message));
    } finally {
      setSaving(null);
    }
  };

  if (accounts === null) {
    return (
      <p className="mt-10 text-sm font-semibold text-slate-500">
        Memuat akun...
      </p>
    );
  }

  return (
    <div className="mt-12">
      <h2 className="font-display text-2xl font-semibold text-[#0B2440]">
        Manajemen Akun Pengelola
      </h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500">
        Atur PIN dan kredensial username/password untuk setiap akun pengelola.
        Akun Perguruan Tinggi Swasta masuk menggunakan username &amp; password.
      </p>

      <div className="mt-6 overflow-hidden rounded-3xl border border-[#E3EBF3] bg-white">
        {accounts.map((a, i) => (
          <div
            key={a.key}
            data-testid={`admin-account-row-${a.key}`}
            className={`flex flex-col gap-4 px-5 py-4 md:px-6 ${
              i > 0 ? "border-t border-[#E3EBF3]" : ""
            }`}
          >
            <div>
              <p className="text-sm font-bold text-[#0B2440]">
                {a.label}
                {a.role === "ppid" && (
                  <span className="ml-2 rounded-full bg-[#E8A33D]/15 px-2 py-0.5 text-[10px] font-bold text-[#B97A18]">
                    ADMIN
                  </span>
                )}
              </p>
              <p
                data-testid={`admin-pin-status-${a.key}`}
                className={`mt-0.5 text-xs font-medium ${
                  a.has_pin ? "text-emerald-600" : "text-slate-400"
                }`}
              >
                {a.key === "pts" ? (
                  <span
                    className={
                      a.has_credentials ? "text-emerald-600" : "text-slate-400"
                    }
                  >
                    {a.has_credentials
                      ? `username: ${a.username}`
                      : "username/password belum diaktifkan"}
                  </span>
                ) : (
                  <>
                    {a.has_pin
                      ? "PIN sudah terpasang"
                      : "PIN belum ditetapkan"}
                    {" · "}
                    <span
                      className={
                        a.has_credentials
                          ? "text-emerald-600"
                          : "text-slate-400"
                      }
                    >
                      {a.has_credentials
                        ? `username: ${a.username}`
                        : "username/password belum diaktifkan"}
                    </span>
                  </>
                )}
              </p>
            </div>

            <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
              {a.key !== "pts" && (
                <div className="flex items-center gap-2">
                  <PasswordInput
                    inputMode="numeric"
                    autoComplete="off"
                    testid={`admin-pin-input-${a.key}`}
                    placeholder="PIN baru (angka, min. 4 digit)"
                    value={pins[a.key] || ""}
                    onChange={(e) =>
                      setPins((p) => ({ ...p, [a.key]: e.target.value }))
                    }
                    className="w-full min-w-0 rounded-xl border border-[#E3EBF3] px-4 py-2.5 text-sm outline-none transition focus:border-[#1479BF] sm:w-56"
                  />
                  <button
                    data-testid={`admin-pin-save-${a.key}`}
                    disabled={
                      saving === `pin-${a.key}` || !(pins[a.key] || "").trim()
                    }
                    onClick={() => savePin(a.key)}
                    className="shrink-0 rounded-full bg-[#1479BF] px-5 py-2.5 text-xs font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-[#0B2440] disabled:opacity-50"
                  >
                    {saving === `pin-${a.key}` ? "..." : "SIMPAN"}
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  data-testid={`admin-username-input-${a.key}`}
                  placeholder="Username"
                  value={creds[a.key]?.username || ""}
                  onChange={(e) =>
                    setCreds((p) => ({
                      ...p,
                      [a.key]: { ...p[a.key], username: e.target.value },
                    }))
                  }
                  className="w-full min-w-0 rounded-xl border border-[#E3EBF3] px-4 py-2.5 text-sm outline-none transition focus:border-[#1479BF] sm:w-40"
                />
                <PasswordInput
                  autoComplete="new-password"
                  testid={`admin-password-input-${a.key}`}
                  placeholder="Password (min. 6)"
                  value={creds[a.key]?.password || ""}
                  onChange={(e) =>
                    setCreds((p) => ({
                      ...p,
                      [a.key]: { ...p[a.key], password: e.target.value },
                    }))
                  }
                  className="w-full min-w-0 rounded-xl border border-[#E3EBF3] px-4 py-2.5 text-sm outline-none transition focus:border-[#1479BF] sm:w-44"
                />
                <button
                  data-testid={`admin-cred-save-${a.key}`}
                  disabled={
                    saving === `cred-${a.key}` ||
                    !(creds[a.key]?.username || "").trim() ||
                    !(creds[a.key]?.password || "")
                  }
                  onClick={() => saveCred(a.key)}
                  className="shrink-0 rounded-full border border-[#1479BF] px-5 py-2.5 text-xs font-bold tracking-[0.08em] text-[#1479BF] transition-colors duration-300 hover:bg-[#EFF6FC] disabled:opacity-50"
                >
                  {saving === `cred-${a.key}` ? "..." : "SIMPAN"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
